import { useEffect, useMemo, useState } from 'react';
import { Card, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';
import { getContestPluginConfig } from '../../../../services/contestPlugin';
import { getSocket } from '../../../../socket';

const ordinal = (place) => {
	if (place === 1) return '1st';
	if (place === 2) return '2nd';
	if (place === 3) return '3rd';
	return `${place}th`;
};

const getContestNameFromEvent = (eventName) => {
	const match = String(eventName || '').trim().match(/^Contest\s+(.+?)\s+Winners$/i);
	return match ? match[1].trim() : '';
};

const normalizeContestName = (value) => String(value || '')
	.trim()
	.toLowerCase()
	.replace(/\s+contest$/, '')
	.replace(/\bspeeches\b/g, 'speech')
	.replace(/\s+/g, ' ');

export default function PollResultItem({ agenda, item, meeting, isTimerManager = false, swipeHandlers }) {
	const { user: authUser } = useSelector((state) => state.auth);
	const meetingId = meeting?.meetingDetails?.id || meeting?.meetingDetails?._id || item?.meeting;
	const contestName = getContestNameFromEvent(item?.agendaEvent?.name);
	const [contestTypeId, setContestTypeId] = useState('');
	const [contestDisplayName, setContestDisplayName] = useState(contestName);
	const [results, setResults] = useState([]);
	const [participantAvatars, setParticipantAvatars] = useState({});
	const [visibleParticipants, setVisibleParticipants] = useState({});
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!meetingId || !authUser?.token) {
			setResults([]);
			setContestTypeId('');
			setContestDisplayName(contestName);
			setParticipantAvatars({});
			setVisibleParticipants({});
			setIsLoading(false);
			return undefined;
		}

		let cancelled = false;
		const loadResults = async () => {
			setIsLoading(true);
			try {
				const config = contestName ? await getContestPluginConfig(authUser.token).catch(() => ({ contestTypes: [] })) : { contestTypes: [] };
				const configuredContest = (config.contestTypes || []).find((contest) => normalizeContestName(contest?.name) === normalizeContestName(contestName));
				const resolvedContestType = String(configuredContest?._id || configuredContest?.id || '').trim();
				if (!cancelled) setContestTypeId(resolvedContestType);
				if (!cancelled && configuredContest?.name) setContestDisplayName(String(configuredContest.name).trim());
				const queryValue = resolvedContestType || contestName;
				const query = queryValue ? `?${new URLSearchParams({ contestType: queryValue }).toString()}` : '';
				const response = await fetch(`${process.env.REACT_APP_URL}/contesthub/meetings/${meetingId}/results${query}`, {
					headers: { Authorization: `Bearer ${authUser.token}` }
				});
				const payload = response.ok ? await response.json() : { results: [] };
				if (!cancelled) setResults(Array.isArray(payload?.results) ? payload.results : []);
			} catch {
				if (!cancelled) setResults([]);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		void loadResults();
		return () => { cancelled = true; };
	}, [authUser?.token, contestName, meetingId]);

	const agendaById = useMemo(() => new Map((Array.isArray(agenda) ? agenda : []).map((agendaItem) => [
		String(agendaItem?._id || agendaItem?.id || agendaItem?.agendaItemId || '').trim(),
		agendaItem
	])), [agenda]);

	const contests = useMemo(() => results
		.filter((result) => !contestName || [contestTypeId, contestName].some((value) => normalizeContestName(result?.contestType) === normalizeContestName(value)))
		.map((result) => {
		const placementsByName = new Map((Array.isArray(result?.placements) ? result.placements : []).map((placement) => [
			String(placement?.name || '').trim().toLowerCase(), placement
		]));
		const rankedNames = [...(Array.isArray(result?.tiebreakerRanking) ? result.tiebreakerRanking : []), ...(Array.isArray(result?.judgeRankings) ? result.judgeRankings.flat() : [])]
			.map((speaker) => String(speaker?.name || '').trim().toLowerCase()).filter(Boolean);
		const assignedIds = Array.isArray(result?.agendaItemIds) ? result.agendaItemIds.map((id) => String(id || '').trim()).filter(Boolean) : [];
		const rankedAgendaItemIds = (Array.isArray(agenda) ? agenda : [])
			.filter((agendaItem) => {
				const name = `${agendaItem?.assignedUser?.firstName || ''} ${agendaItem?.assignedUser?.lastName || ''}`.trim().toLowerCase();
				return name && rankedNames.includes(name);
			})
			.map((agendaItem) => String(agendaItem?._id || agendaItem?.id || agendaItem?.agendaItemId || '').trim()).filter(Boolean);
		const participantItems = [];
		const participantKeys = new Set();
		[...assignedIds, ...rankedAgendaItemIds].forEach((agendaItemId) => {
			const agendaItem = agendaById.get(String(agendaItemId || '').trim());
			const userId = String(agendaItem?.assignedUser?.tmAuthUserId || agendaItem?.assignedUser?.id || agendaItem?.assignedUser?._id || '').trim();
			const speakerName = `${agendaItem?.assignedUser?.firstName || ''} ${agendaItem?.assignedUser?.lastName || ''}`.trim().toLowerCase();
			const participantKey = userId || speakerName || String(agendaItemId || '').trim();
			if (!participantKey || participantKeys.has(participantKey)) return;
			participantKeys.add(participantKey);
			participantItems.push({ agendaItemId, agendaItem });
		});
		const participants = participantItems.map(({ agendaItemId, agendaItem }, index) => {
			const firstName = String(agendaItem?.assignedUser?.firstName || '').trim();
			const lastName = String(agendaItem?.assignedUser?.lastName || '').trim();
			const name = `${agendaItem?.assignedUser?.firstName || ''} ${agendaItem?.assignedUser?.lastName || ''}`.trim() || `Participant ${index + 1}`;
			const placement = placementsByName.get(name.toLowerCase());
			return {
				id: String(agendaItemId || index),
				userId: String(agendaItem?.assignedUser?.tmAuthUserId || agendaItem?.assignedUser?.id || agendaItem?.assignedUser?._id || '').trim(),
				firstName,
				lastName,
				name,
				speechName: placement?.speechName || String(agendaItem?.item_title || '').split(/\r?\n/).map((line) => line.trim()).find(Boolean) || '',
				avatarSrc: resolveAvatarSrc(agendaItem?.assignedUser?.avatarUrl || agendaItem?.assignedUser?.avatar || ''),
				initials: getUserInitials(agendaItem?.assignedUser, name),
				place: placement?.place || null,
				points: placement?.points || 0
			};
		});
		return {
			...result,
			participants: participants.sort((left, right) => (left.place || Infinity) - (right.place || Infinity)),
			winners: participants.filter((participant) => participant.place && participant.place <= 3),
			otherParticipants: participants
				.filter((participant) => !participant.place || participant.place > 3)
				.sort((left, right) => left.firstName.localeCompare(right.firstName) || left.lastName.localeCompare(right.lastName) || left.name.localeCompare(right.name))
		};
	}), [agenda, agendaById, contestName, contestTypeId, results]);

	const toggleParticipantVisibility = (participantId) => {
		const visible = !visibleParticipants[participantId];
		setVisibleParticipants((current) => ({ ...current, [participantId]: visible }));
		if (isTimerManager && meetingId && item?.agendaItemId) {
			getSocket().emit('meeting:contest-participant-visibility', {
				meetingId,
				agendaItemId: item.agendaItemId,
				participantId,
				visible,
			});
		}
	};

	useEffect(() => {
		let mounted = true;
		const participantIds = contests.flatMap((contest) => contest.participants.map((participant) => participant.userId)).filter(Boolean);
		participantIds.forEach((userId) => {
			if (participantAvatars[userId]) return;
			fetch(`${process.env.REACT_APP_URL}/unified/${encodeURIComponent(userId)}`)
				.then((response) => response.ok ? response.json() : null)
				.then((payload) => {
					const avatar = payload?.identity?.avatarUrl || payload?.identity?.avatar || null;
					const src = avatar ? resolveAvatarSrc(avatar) : null;
					if (src && mounted) setParticipantAvatars((current) => ({ ...current, [userId]: src }));
				})
				.catch(() => {});
		});
		return () => { mounted = false; };
	}, [contests, participantAvatars]);

	useEffect(() => {
		if (!meetingId || !item?.agendaItemId) return undefined;
		const socket = getSocket();
		const handleParticipantVisibility = (payload) => {
			if (String(payload?.meetingId || '') !== String(meetingId)
				|| String(payload?.agendaItemId || '') !== String(item.agendaItemId)
				|| !payload?.participantId) return;
			setVisibleParticipants((current) => ({ ...current, [String(payload.participantId)]: payload.visible === true }));
		};
		socket.on('meeting:contest-participant-visibility', handleParticipantVisibility);
		// Hydrate with whatever visibility state the timer manager last set, instead of
		// defaulting back to hidden every time this modal is (re)opened.
		socket.emit('meeting:contest-participant-visibility-request', { meetingId, agendaItemId: item.agendaItemId });
		return () => socket.off('meeting:contest-participant-visibility', handleParticipantVisibility);
	}, [item?.agendaItemId, meetingId]);

	const renderParticipantCard = (participant, isWinner) => {
		const isVisible = visibleParticipants[participant.id];
		return (
			<article key={participant.id} className={`contest-results-participant${isWinner ? ` contest-results-participant--place-${participant.place}` : ' contest-results-participant--regular'}`}>
				{isTimerManager && <button type="button" className="contest-results-participant-toggle" onClick={() => toggleParticipantVisibility(participant.id)} aria-label={isVisible ? `Hide ${participant.name}` : `Show ${participant.name}`} title={isVisible ? 'Hide participant' : 'Show participant'}><i className={`bi ${isVisible ? 'bi-eye' : 'bi-eye-slash'}`} aria-hidden="true" /></button>}
				{isVisible && (participant.avatarSrc || participantAvatars[participant.userId]) ? <img src={participant.avatarSrc || participantAvatars[participant.userId]} alt={participant.name} className="contest-results-participant-avatar" /> : isVisible ? <span className="contest-results-participant-avatar contest-results-participant-avatar--fallback" aria-hidden="true">{participant.initials}</span> : <span className="contest-results-participant-avatar contest-results-participant-avatar--hidden" aria-hidden="true"><i className="bi bi-eye-slash" /></span>}
				<div className="contest-results-participant-details">
					<span className="contest-results-participant-place">{isWinner ? `${ordinal(participant.place)} Place` : 'Participant'}</span>
					{isVisible && <><strong>{participant.name}</strong>{participant.speechName && <span className="contest-results-participant-speech">"{participant.speechName}"</span>}</>}
				</div>
			</article>
		);
	};

	return (
		<Modal.Body className="timer-header contest-results-modal-body" {...(swipeHandlers || {})}>
			<Card className="contest-results-modal-shell">
				<Card.Body>
					<h1>{contestDisplayName || item?.item_title || 'Contest Results'}</h1>
					{isLoading ? <Spinner animation="border" role="status" /> : contests.length === 0 ? (
						<p className="contest-results-empty">Results are not available yet.</p>
					) : contests.map((contest) => (
						<section className="contest-results-contest" key={contest.contestType}>
							{contests.length > 1 && <h2>{contestDisplayName || contest.contestType}</h2>}
							<h2>{contest.placements.length > 0 ? 'Calculated Results' : 'Contest Participants'}</h2>
							{contest.placements.length === 0 && <p className="contest-results-empty">Results are not available yet. The participants are shown below.</p>}
							<div className="contest-results-roster">
								{contest.winners.map((participant) => renderParticipantCard(participant, true))}
								{contest.winners.length > 0 && contest.otherParticipants.length > 0 && <>
										<div className="contest-results-participant-separator" role="separator">
											<p>We'd like to thank our other participants for their performance and the following results are displayed sorted alphabetically based on first name, not based on scoring.</p>
										</div>
										{contest.otherParticipants.map((participant) => renderParticipantCard(participant, false))}
									</>}
							</div>
						</section>
					))}
				</Card.Body>
			</Card>
		</Modal.Body>
	);
}
